import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CONFIG } from '../utils/config';
import {
  requestEmailConfirmation,
  rememberPendingEmail,
  clearPendingEmail,
  getPendingEmail,
} from '../services/accountService';
import { usePayment } from '../hooks/usePayment';
import { useCart } from '../contexts/CartContext';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';

const Login = () => {
  const [email, setEmail] = useState(() => getPendingEmail());
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendFeedback, setResendFeedback] = useState({ type: '', message: '' });
  const navigate = useNavigate();
  const location = useLocation();
  const { processStoredPaymentIntent } = usePayment();
  const { loadCart } = useCart();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get('message');
    if (message) {
      setCustomMessage(decodeURIComponent(message));
    }
  }, [location.search]);

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendFeedback({ type: '', message: '' });
    try {
      const data = await requestEmailConfirmation(email);
      setResendFeedback({
        type: 'success',
        message: data?.message || content.messages.resendSuccess,
      });
    } catch (err) {
      setResendFeedback({
        type: 'error',
        message: err.message || content.messages.resendError,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setNeedsVerification(false);
    setResendFeedback({ type: '', message: '' });
    try {
      if (!email || !password) {
        setError(content.messages.missingFields);
        return;
      }
      const normalizedEmail = email.trim().toLowerCase();
      rememberPendingEmail(normalizedEmail);
      const res = await fetch(`${CONFIG.BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mail: normalizedEmail, password }),
      });
      const ct = res.headers.get('content-type') || '';
      const isJson = ct.includes('application/json');
      const data = isJson ? await res.json() : {};
      if (!res.ok) {
        const msg = data?.error || `Connexion echouee (${res.status})`;
        throw new Error(msg);
      }
      const token = data?.access;
      if (!token) throw new Error('Reponse invalide : jeton manquant');
      localStorage.setItem('quikr_access_token', token);
      document.cookie = `quikr_access_token=${token}; path=/; max-age=86400`;
      localStorage.setItem('quikr_email_verified', 'true');
      clearPendingEmail();
      await loadCart();
      const hasPaymentIntent = await processStoredPaymentIntent();
      if (!hasPaymentIntent) {
        const urlParams = new URLSearchParams(location.search);
        const redirectUrl = urlParams.get('redirect');
        const from = redirectUrl || location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    } catch (err) {
      const message = err.message || content.messages.genericError;
      setError(message);
      if (message.toLowerCase().includes('email not verified')) {
        setNeedsVerification(true);
        localStorage.setItem('quikr_email_verified', 'false');
      try {
        const data = await requestEmailConfirmation(email);
        setResendFeedback({
          type: 'success',
          message: data?.message || content.messages.resendSuccess,
        });
      } catch (err2) {
        setResendFeedback({
          type: 'error',
          message: err2.message || content.messages.resendError,
        });
      }
      }
    } finally {
      setIsLoading(false);
    }
  };
	
  const { isDark } = useTheme();
  const { content, loading: contentLoading } = useContent('login');

  if (contentLoading || !content) {

    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen flex items-center justify-center relative overflow-hidden">
      <div
        className="absolute inset-0 bg-no-repeat chameleon-background pointer-events-none"
        style={{
          backgroundImage: 'url(/images/Cameleon_HD.jpg)',
          backgroundPosition: 'calc(100% + 100px) center'
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-l from-dark-900/0 via-dark-900/70 to-dark-900 pointer-events-none"></div>
      <div className="relative max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img 
              src="/images/logo.png" 
              alt="Quikr.it Logo" 
              className="w-40 h-20 object-contain"
            />
          </Link>

          <p className="text-gray-400 mt-2">
            {customMessage || content.pageTitle}
          </p>
        </div>

        <div className="bg-dark-800 p-8 rounded-xl border border-dark-700">
          {customMessage && (
            <div className="mb-4 bg-primary-900/40 border border-primary-600 text-primary-200 px-4 py-3 rounded-lg text-sm">
              {customMessage}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
            {needsVerification && (
              <div className="bg-amber-900/40 border border-amber-600 text-amber-200 px-4 py-3 rounded-lg text-sm space-y-3">
                <p>{content.messages.emailNotVerified}</p>
                
                {/* {resendFeedback.message && (
                  <p className={resendFeedback.type === 'success' ? 'text-green-300' : 'text-red-300'}>
                    {resendFeedback.message}
                  </p>
                )} */}
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isResending ? content.messages.resending : content.messages.resendButton}
                </button>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {content.form.emailLabel}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder={content.form.emailPlaceholder}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
		  {content.form.passwordLabel}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                placeholder={content.form.passwordPlaceholder}
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-dark-600 text-primary-600 focus:ring-primary-500 bg-dark-700" />
                <span className="ml-2 text-sm text-gray-300">{content.form.rememberMe}</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-primary-400 hover:text-primary-300">
                 {content.form.forgotPassword}
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
		      {content.form.submitting}
                </div>
              ) : (
	      content.form.submitButton
              )}
            </button>
          </form>

          {/* <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dark-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-dark-800 text-gray-400">Ou continuer avec</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button className="w-full inline-flex justify-center py-2 px-4 border border-dark-600 rounded-lg shadow-sm bg-dark-700 text-sm font-medium text-gray-300 hover:bg-dark-600 transition-colors duration-200">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="ml-2">Google</span>
              </button>

              <button className="w-full inline-flex justify-center py-2 px-4 border border-dark-600 rounded-lg shadow-sm bg-dark-700 text-sm font-medium text-gray-300 hover:bg-dark-600 transition-colors duration-200">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="ml-2">Facebook</span>
              </button>
            </div>
          </div> */}

          <p className="mt-6 text-center text-sm text-gray-400">
	    {content.footer.noAccount}{' '}
            <Link to="/signup" className="text-primary-400 hover:text-primary-300">
	    {content.footer.createAccount}
            </Link>
          </p>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-gray-400 hover:text-white text-sm">
	    {content.footer.backHome}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;


